import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import { useLoc } from "@/lib/loc";
import { Film, Eye, Clock, TrendingUp, AlertTriangle, Loader2, Download } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, Cell, AreaChart, Area, CartesianGrid } from "recharts";

function Stat({ icon: Icon, label, value, sub }) {
  return (
    <div className="rounded-xl border border-white/5 bg-[hsl(var(--surface-elevated))] p-5">
      <div className="flex items-center gap-2 text-muted-foreground">
        <Icon className="h-4 w-4 text-primary" />
        <span className="text-xs uppercase tracking-widest">{label}</span>
      </div>
      <p className="mt-3 font-heading text-3xl font-bold">{value}</p>
      {sub && <p className="mt-1 text-xs text-muted-foreground">{sub}</p>}
    </div>
  );
}

export default function AdminDashboard() {
  const { t } = useTranslation();
  const loc = useLoc();
  const { data, isLoading } = useQuery({ queryKey: ["analytics"], queryFn: async () => (await api.get("/admin/analytics/summary")).data });
  const { data: ts } = useQuery({ queryKey: ["analytics-ts"], queryFn: async () => (await api.get("/admin/analytics/timeseries", { params: { days: 30 } })).data });
  const { data: alerts } = useQuery({ queryKey: ["alerts"], queryFn: async () => (await api.get("/admin/alerts")).data });

  const today = new Date().toISOString().slice(0, 10);
  const monthAgo = new Date(Date.now() - 30 * 864e5).toISOString().slice(0, 10);
  const [expStart, setExpStart] = useState(monthAgo);
  const [expEnd, setExpEnd] = useState(today);
  const exportCsv = async (dataset) => {
    try {
      const res = await api.get("/admin/analytics/export", {
        params: { dataset, start: expStart, end: expEnd }, responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = `vytaliflix_${dataset}.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch (_) {}
  };

  if (isLoading) return <div className="flex h-96 items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;

  const chartData = (data?.top_content || []).map((v) => ({ name: loc(v.title).slice(0, 14), views: v.views }));
  return (
    <div>
      <h1 className="font-heading text-2xl font-bold tracking-tight">{t("admin.dashboard")}</h1>

      <div className="mt-4 flex flex-wrap items-end gap-3 rounded-xl border border-white/5 bg-[hsl(var(--surface-elevated))] p-4" data-testid="analytics-export">
        <div>
          <label className="mb-1 block text-[11px] uppercase tracking-widest text-muted-foreground">Du</label>
          <input type="date" value={expStart} onChange={(e) => setExpStart(e.target.value)} data-testid="export-start" className="rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm outline-none" />
        </div>
        <div>
          <label className="mb-1 block text-[11px] uppercase tracking-widest text-muted-foreground">Au</label>
          <input type="date" value={expEnd} onChange={(e) => setExpEnd(e.target.value)} data-testid="export-end" className="rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm outline-none" />
        </div>
        <div className="flex flex-wrap gap-2">
          {[["overview", "Vues / Temps / Complétion"], ["traffic_sources", "Sources"], ["top_videos", "Top vidéos"]].map(([ds, label]) => (
            <button key={ds} onClick={() => exportCsv(ds)} data-testid={`export-${ds}`} className="flex items-center gap-1.5 rounded-full bg-primary/15 px-3 py-2 text-xs font-semibold text-primary transition-colors hover:bg-primary/25">
              <Download className="h-3.5 w-3.5" /> {label} CSV
            </button>
          ))}
        </div>
      </div>

      <div className="mt-6 grid grid-cols-2 gap-4 lg:grid-cols-5">
        <Stat icon={Film} label={t("admin.totalVideos")} value={data.total_videos} sub={`${data.published_videos} ${t("admin.published").toLowerCase()}`} />
        <Stat icon={Eye} label={t("admin.totalViews")} value={data.total_views.toLocaleString("fr-FR")} />
        <Stat icon={Clock} label={t("admin.watchTime")} value={`${data.total_watch_time_hours}h`} />
        <Stat icon={TrendingUp} label={t("admin.completionRate")} value={`${data.avg_completion_rate}%`} />
        <Stat icon={Film} label={t("admin.published")} value={data.published_videos} />
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="rounded-xl border border-white/5 bg-[hsl(var(--surface-elevated))] p-5 lg:col-span-2">
          <h2 className="mb-4 font-heading text-lg font-semibold">{t("admin.topContent")}</h2>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={chartData}>
              <XAxis dataKey="name" tick={{ fill: "#A1A1AA", fontSize: 11 }} interval={0} angle={-20} textAnchor="end" height={60} />
              <YAxis tick={{ fill: "#A1A1AA", fontSize: 11 }} />
              <Tooltip contentStyle={{ background: "#1A1A1E", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, color: "#fff" }} cursor={{ fill: "rgba(255,255,255,0.04)" }} />
              <Bar dataKey="views" radius={[6, 6, 0, 0]}>
                {chartData.map((_, i) => <Cell key={i} fill="#F5A524" />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="rounded-xl border border-white/5 bg-[hsl(var(--surface-elevated))] p-5">
          <h2 className="mb-4 flex items-center gap-2 font-heading text-lg font-semibold">
            <AlertTriangle className="h-4 w-4 text-primary" /> {t("admin.alerts")}
          </h2>
          {alerts?.length ? (
            <ul className="space-y-2">
              {alerts.map((a) => (
                <li key={a.id} className="rounded-lg border border-destructive/20 bg-destructive/10 px-3 py-2 text-sm text-destructive/90">
                  {a.message}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-muted-foreground">{t("admin.noAlerts")}</p>
          )}
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="rounded-xl border border-white/5 bg-[hsl(var(--surface-elevated))] p-5 lg:col-span-2">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="font-heading text-lg font-semibold">Vues & temps de visionnage (30 j)</h2>
            <span className="text-xs text-muted-foreground">Complétion : {ts?.completion_rate ?? 0}%</span>
          </div>
          <ResponsiveContainer width="100%" height={240}>
            <AreaChart data={ts?.series || []}>
              <defs>
                <linearGradient id="gv" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#F5A524" stopOpacity={0.5} /><stop offset="95%" stopColor="#F5A524" stopOpacity={0} /></linearGradient>
                <linearGradient id="gw" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#6ea8fe" stopOpacity={0.4} /><stop offset="95%" stopColor="#6ea8fe" stopOpacity={0} /></linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="date" tick={{ fill: "#A1A1AA", fontSize: 10 }} tickFormatter={(d) => d?.slice(5)} />
              <YAxis tick={{ fill: "#A1A1AA", fontSize: 10 }} />
              <Tooltip contentStyle={{ background: "#1A1A1E", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, color: "#fff" }} />
              <Area type="monotone" dataKey="views" name="Vues" stroke="#F5A524" fill="url(#gv)" strokeWidth={2} />
              <Area type="monotone" dataKey="watch_time_hours" name="Heures" stroke="#6ea8fe" fill="url(#gw)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="rounded-xl border border-white/5 bg-[hsl(var(--surface-elevated))] p-5">
          <h2 className="mb-4 font-heading text-lg font-semibold">Sources de trafic</h2>
          {ts?.traffic_sources?.length ? (
            <ul className="space-y-2" data-testid="traffic-sources">
              {ts.traffic_sources.map((s) => {
                const max = ts.traffic_sources[0].views || 1;
                return (
                  <li key={s.source}>
                    <div className="mb-1 flex justify-between text-xs"><span className="truncate text-foreground/90">{s.source}</span><span className="text-muted-foreground">{s.views}</span></div>
                    <div className="h-1.5 w-full overflow-hidden rounded-full bg-white/5"><div className="h-full bg-primary" style={{ width: `${(s.views / max) * 100}%` }} /></div>
                  </li>
                );
              })}
            </ul>
          ) : <p className="text-sm text-muted-foreground">Pas encore de données de trafic.</p>}
        </div>
      </div>

      <div className="mt-6 rounded-xl border border-white/5 bg-[hsl(var(--surface-elevated))] p-5">
        <h2 className="mb-4 font-heading text-lg font-semibold">{t("admin.topContent")}</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/5 text-left text-xs uppercase tracking-widest text-muted-foreground">
                <th className="py-2 pr-4">Titre</th>
                <th className="py-2 pr-4">{t("admin.totalViews")}</th>
                <th className="py-2 pr-4">{t("admin.watchTime")}</th>
                <th className="py-2 pr-4">{t("admin.completionRate")}</th>
              </tr>
            </thead>
            <tbody>
              {data.top_content.map((v) => (
                <tr key={v.id} className="border-b border-white/5 transition-colors hover:bg-white/5">
                  <td className="py-2 pr-4 font-medium">{loc(v.title)}</td>
                  <td className="py-2 pr-4">{v.views.toLocaleString("fr-FR")}</td>
                  <td className="py-2 pr-4">{Math.round(v.watch_time_total / 60)} min</td>
                  <td className="py-2 pr-4">{v.completion_rate}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
